import { AiAgentWbsItemRef } from '../lib/functions/ai'

// AIエージェントチャットへ添付できるWBSアイテム参照の上限。上限を超える分は追加しない
// （呼び出し側はこの定数を使って、超過した旨をユーザーに伝えるかどうかを判定する）。
export const MAX_WBS_ITEM_ATTACHMENTS = 50

enum ActionType {
  ADD_WBS_ITEM_ATTACHMENTS = 'aiAgentChat/ADD_WBS_ITEM_ATTACHMENTS',
  REMOVE_WBS_ITEM_ATTACHMENT = 'aiAgentChat/REMOVE_WBS_ITEM_ATTACHMENT',
  CLEAR_WBS_ITEM_ATTACHMENTS = 'aiAgentChat/CLEAR_WBS_ITEM_ATTACHMENTS',
  REQUEST_OPEN_CHAT = 'aiAgentChat/REQUEST_OPEN_CHAT',
}

type State = {
  // 画面側からpushされた、送信前のWBSアイテム添付一覧。
  wbsItemAttachments: AiAgentWbsItemRef[]
  // 「チャットを開いてほしい」という画面側からの要求の発行回数。
  // 開閉状態そのものはAiAgentChatが持つため、ここでは要求が起きたことだけを伝える
  // （値の増加が1回の要求にあたる。同じ操作を続けて行っても取りこぼさない）。
  openRequestSeq: number
}

const initialState: State = {
  wbsItemAttachments: [],
  openRequestSeq: 0,
}

export const addWbsItemAttachments = (items: AiAgentWbsItemRef[]) => ({
  type: ActionType.ADD_WBS_ITEM_ATTACHMENTS as const,
  payload: items,
})

export const removeWbsItemAttachment = (code: string) => ({
  type: ActionType.REMOVE_WBS_ITEM_ATTACHMENT as const,
  payload: code,
})

export const clearWbsItemAttachments = () => ({
  type: ActionType.CLEAR_WBS_ITEM_ATTACHMENTS as const,
})

export const requestOpenChat = () => ({
  type: ActionType.REQUEST_OPEN_CHAT as const,
})

type Action =
  | ReturnType<typeof addWbsItemAttachments>
  | ReturnType<typeof removeWbsItemAttachment>
  | ReturnType<typeof clearWbsItemAttachments>
  | ReturnType<typeof requestOpenChat>

// 追加対象のうち、まだ添付されていないもの（code重複なし）だけを抽出する。
const pickNewItems = (
  current: AiAgentWbsItemRef[],
  toAdd: AiAgentWbsItemRef[]
): AiAgentWbsItemRef[] => {
  const existingCodes = new Set(current.map(item => item.code))
  return toAdd.filter(item => !existingCodes.has(item.code))
}

// 上限を超えて追加されなかった件数。0より大きい場合、呼び出し側はユーザーに通知する
// （重複除去だけで結果的に追加0件になったケースは上限超過ではないため区別する）。
export const countWbsItemAttachmentsRejectedByLimit = (
  current: AiAgentWbsItemRef[],
  toAdd: AiAgentWbsItemRef[]
): number => {
  const newItems = pickNewItems(current, toAdd)
  const acceptableCount = Math.max(0, MAX_WBS_ITEM_ATTACHMENTS - current.length)
  return Math.max(0, newItems.length - acceptableCount)
}

export const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case ActionType.ADD_WBS_ITEM_ATTACHMENTS: {
      const newItems = pickNewItems(state.wbsItemAttachments, action.payload)
      const acceptableCount = Math.max(
        0,
        MAX_WBS_ITEM_ATTACHMENTS - state.wbsItemAttachments.length
      )
      if (newItems.length === 0 || acceptableCount === 0) {
        return state
      }
      return {
        ...state,
        wbsItemAttachments: [
          ...state.wbsItemAttachments,
          ...newItems.slice(0, acceptableCount),
        ],
      }
    }

    case ActionType.REMOVE_WBS_ITEM_ATTACHMENT:
      return {
        ...state,
        wbsItemAttachments: state.wbsItemAttachments.filter(
          item => item.code !== action.payload
        ),
      }

    case ActionType.CLEAR_WBS_ITEM_ATTACHMENTS:
      return { ...state, wbsItemAttachments: [] }

    case ActionType.REQUEST_OPEN_CHAT:
      return { ...state, openRequestSeq: state.openRequestSeq + 1 }

    default:
      return state
  }
}

// Selectors
export const selectWbsItemAttachments = (state: {
  aiAgentChat: State
}): AiAgentWbsItemRef[] => state.aiAgentChat.wbsItemAttachments

export const selectOpenRequestSeq = (state: { aiAgentChat: State }): number =>
  state.aiAgentChat.openRequestSeq
